import React, {useCallback, useEffect, useState} from 'react';
import {Card} from 'primereact/card';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Divider} from 'primereact/divider';
import {Skeleton} from 'primereact/skeleton';
import StorageHelper from '../../../../utils/storage-helper';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate-no-encryption';

const LoadingSkeleton = () => (
    <div className="border-round border-1 surface-border p-4 surface-card">
        <div className="flex mb-3">
            <Skeleton shape="circle" size="4rem" className="mr-2"/>
            <div>
                <Skeleton width="10rem" className="mb-2"/>
                <Skeleton width="5rem" className="mb-2"/>
                <Skeleton height=".5rem"/>
            </div>
        </div>
        <Skeleton width="100%" height="150px"/>
        <div className="flex justify-content-between mt-3">
            <Skeleton width="4rem" height="2rem"/>
            <Skeleton width="4rem" height="2rem"/>
        </div>
    </div>
);

function PolicyHistoryTable({balanceFormatted}) {
    const [loading, setLoading] = useState(true);
    const [historyData, setHistoryData] = useState([]);

    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_DOVESAPI}policy/history/${balanceFormatted.Policy_Number}`;
            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${StorageHelper.get('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            const data = await response.json();

            if (data.status === 'SUCCESS') {
                setHistoryData(data.responseBody || []);
            } else {
                console.error('Failed to fetch policy history:', data.message);
            }
        } catch (error) {
            console.error('Error fetching policy history:', error);
        } finally {
            setLoading(false);
        }
    }, [balanceFormatted.Policy_Number]);

    useEffect(() => {
        if (balanceFormatted?.Policy_Number) {
            fetchHistory();
        }
    }, [fetchHistory, balanceFormatted]);

    const handleExportExcel = async () => {
        if (!historyData?.length) return;

        try {
            const rows = historyData.map((item) => ([
                item.receiptNumber,
                item.receiptDate,
                item.receiptAmount,
                item.paymentMethod,
                item.monthsPaidInAdvance,
                item.remarks
            ]));
            const workbook = await XlsxPopulate.fromBlankAsync();
            const sheet = workbook.sheet(0).name('Payment History');

            sheet.cell('A1').value([
                [
                    'Receipt Number',
                    'Receipt Date',
                    'Receipt Amount',
                    'Payment Method',
                    'Months Paid In Advance',
                    'Remarks'
                ],
                ...rows
            ]);

            const blob = await workbook.outputAsync();
            const fileName = `receipt_history_${balanceFormatted?.Policy_Number || 'export'}.xlsx`;

            if (window.navigator?.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, fileName);
                return;
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting policy history:', error);
        }
    };

    if (loading) {
        return <LoadingSkeleton/>;
    }

    if (!historyData || historyData.length === 0) {
        return null;
    }

    return (
        <Card className="mb-5 mt-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 px-2">
                <div>
                    <span className="text-xs font-bold text-base-content/60 uppercase tracking-widest mb-1 block">Payment History</span>
                    <h2 className="text-xl font-bold text-base-content mb-1">Your Payment Statement</h2>
                    <p className="text-sm text-base-content/70">A record of receipts made against this policy.</p>
                </div>
                <button
                    type="button"
                    className="btn btn-outline btn-info rounded-full border-2"
                    onClick={handleExportExcel}
                >
                    <i className="pi pi-file-excel text-lg mr-2"></i> Export Excel
                </button>
            </div>
            <Divider type="dotted" className="opacity-50"/>
            <DataTable
                value={historyData}
                paginator
                rows={5}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{minWidth: '50rem'}}
            >
                <Column field="receiptNumber" header="Receipt Number"/>
                <Column field="receiptDate" header="Receipt Date"/>
                <Column field="receiptAmount" header="Receipt Amount"/>
                <Column field="paymentMethod" header="Payment Method"/>
                <Column field="monthsPaidInAdvance" header="Months Paid In Advance"/>
                <Column field="remarks" header="Remarks"/>
            </DataTable>
        </Card>
    );
}

export default PolicyHistoryTable;
